import React, { createContext, useContext, useState, useRef, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { seatTemplateService, ENTITY_STATUS_MAP, JetsContext, ENTITY_TYPE_MAP, JetsSeatMap } from '@seatmaps.com/react-lib';
import { IconFlex, Text, Notice, Button, Tooltip, Popover, ActivityIndicator, ToggleGroup, Modal, Fade, Tray, AirlineLogo, AirportCode, Tabs } from '@amtrav/view-components';
import styled, { createGlobalStyle, css, ThemeContext } from 'styled-components';
import { Flex, Box } from '@rebass/grid';
import { getThemeProp, useFineInput, MatchMedia, media, isMatchedMaxWidth } from '@amtrav/themes';
import { IconWarningTriangle, IconLiveTV, IconPersonalVideo, IconStreamingVideo, IconUSB, IconPower, IconCheckCircle, IconClear, IconLine, IconPrevious, IconNext } from '@amtrav/icons';
import { SPACE, getTokenValue, THEME_TOKENS } from '@amtrav/theme-system';

const JETS_API_URL = '/api/v1/SeatMap';
const JETS_APP_ID = 'a2b';
const SeatConfig = {
  width: 500,
  horizontal: false,
  rightToLeft: false,
  visibleFuselage: true,
  visibleWings: true,
  builtInDeckSelector: false,
  singleDeckMode: true,
  visibleCabinTitles: true,
  // cabinTitlesWidth: 85,

  builtInTooltip: false,
  externalPassengerManagement: true,
  tooltipOnHover: true,
  lang: 'EN',
  units: 'imperials',
  apiUrl: JETS_API_URL,
  apiAppId: JETS_APP_ID,
  colorTheme: {
    deckLabelTitleColor: 'black',
    deckHeightSpacing: 0,
    wingsWidth: 50,
    deckSeparation: 0,
    floorColor: '#fff',
    seatLabelColor: 'black',
    seatStrokeColor: '#ccc',
    seatStrokeWidth: 3,
    seatArmrestColor: 'darkgrey',
    notAvailableSeatsColor: '#ddd',
    bulkBaseColor: '#aaa',
    bulkCutColor: '#ccc',
    bulkIconColor: 'darkslategray',
    defaultPassengerBadgeColor: 'darkred',
    defaultPassengerBadgeBorderColor: 'transparent',
    fontFamily: 'sans-serif',
    tooltipBackgroundColor: 'rgb(255,255,255)',
    tooltipHeaderColor: '#4f6f8f',
    tooltipBorderColor: 'rgb(255,255,255)',
    tooltipFontColor: '#4f6f8f',
    tooltipIconColor: '#4f6f8f',
    tooltipIconBorderColor: '#4f6f8f',
    tooltipIconBackgroundColor: '#fff',
    tooltipSelectButtonTextColor: '#fff',
    tooltipSelectButtonBackgroundColor: 'rgb(42, 85, 128)',
    tooltipCancelButtonTextColor: '#fff',
    tooltipCancelButtonBackgroundColor: 'rgb(55, 55, 55)',
    deckSelectorStrokeColor: '#fff',
    deckSelectorFillColor: 'rgba(55, 55, 55, 0.5)',
    deckSelectorSize: 30,
    fuselageStrokeWidth: 12,
    fuselageFillColor: '#fff',
    fuselageStrokeColor: '#d6d6d6',
    fuselageWindowsColor: '#d6d6d6',
    fuselageWingsColor: 'rgba(55, 55, 55, 0.5)',
    exitIconUrlLeft: 'https://cdn.amtrav.com/airline-seat-map/icons/exit-arrow-left.svg',
    exitIconUrlRight: 'https://cdn.amtrav.com/airline-seat-map/icons/exit-arrow-right.svg'
  }
};

const OutOfPolicyIconWrapper = styled.div`
  background: ${getThemeProp('global.warning--color')};
  color: white;
  font-size: 28px;
  position: absolute;
  top: -29px;
  border-radius: 20px;
  z-index: 2;
  padding: 2px;
  left: calc(50% - -22px);
  display: block;
  height: 34px;
  width: 34px;

  && svg {
    width: 1em;
    height: 1em;
  }
`;
const OutOfPolicyIcon = () => /*#__PURE__*/React.createElement(OutOfPolicyIconWrapper, null, /*#__PURE__*/React.createElement(IconWarningTriangle, null));

const SeatStyles = createGlobalStyle`
.jets-seat-number {
  top: 24%;
}
.ST-5,
.ST-6 {
  top: 28%;
}
.ST-8 {
  margin-left: 12px;
}
.ST-9 {
  margin-right: 12px;
}

.ST-10 {
  margin-left: 60px;
}
.ST-11 {
  margin-right: 60px;
}

.ST-12 {
  top: 25%;
  margin-right: 106px;
}
.ST-13 {
  top: 25%;
  margin-left: 106px;
}

.ST-15 {
  margin-left: 38px;
}
.ST-16 {
  margin-right: 38px;
}
.ST-17 {
  margin-left: 72px;
}
.ST-18 {
  margin-right: 72px;
}

.ST-20,
.ST-21 {
  top: 65%;
}

.ST-22,
.ST-23 {
  top: 60%;
}
.ST-24,
.ST-25 {
  top: 55%;
}
.ST-26 {
  top: 30%;
  margin-right: 50px;
}
.ST-27 {
  top: 30%;
  margin-left: 50px;
}
.ST-28 {
  top: 45%;
  margin-right: 62px;
}
.ST-29 {
  top: 45%;
  margin-left: 62px;
}
.ST-30 {
  top: 60%;
  margin-right: 62px;
}
.ST-31 {
  top: 60%;
  margin-left: 62px;
}
.ST-32 {
  top: 60%;
  margin-right: 58px;
}
.ST-33 {
  top: 60%;
  margin-left: 58px;
}
.ST-34 {
  top: 40%;
  margin-left: 27px;
}
.ST-35 {
  top: 40%;
  margin-right: 27px;
}
.ST-36 {
  top: 62%;
  margin-right: 122px;
}
.ST-37 {
  top: 62%;
  margin-left: 122px;
}
.ST-38 {
  top: 28%;
  margin-right: 122px;
}
.ST-39 {
  top: 28%;
  margin-left: 122px;
}

.ST-42 {
  top: 70%;
  margin-left: 260px;
}
.ST-43 {
  top: 70%;
  margin-right: 242px;
}
.ST-44 {
  top: 18%;
  margin-right: 254px;
}
.ST-45 {
  top: 18%;
  margin-left: 260px;
}
.jets-seat-map svg {
  height: 100%;
  width: 100%;   
}
.bulk__icon svg {
  height: unset;
  width: unset;
}
.jets-seat-map {
  margin: 32px auto;
}
.jets-seat-number b {
  font-family: monospace;
  font-size: 26px;
  display: inline-flex;
  justify-content: center;
  border-radius: 8px;
  font-weight: normal;
  height: 32px;
}
.jets-unavailable {
  opacity: 0.6;
}
.jets-unavailable .jets-seat-number b {
  opacity: 0.4;
  color: #999;
}
.jets-tooltip--features > ul > li > div,
.jets-tooltip--features > ul > li {
  color: white;
}
.jets-deck--title {
  display: none;
}
.deck-exit__image {
  height: 58px;
  width: 58px;
}
.deck-exit:has(img[src$="right.svg"]) {
  transform: translateX(86px);
  z-index: 0;
  filter: drop-shadow(white 0px 0px 10px);
}
.deck-exit:has(img[src$="left.svg"]) {
  transform: translateX(-72px);
  z-index: 0;
  filter: drop-shadow(white 0px 0px 10px);
}
.jets-wings {
  transform: unset; /* unset jet-seat styles to fix for mobile Safari zoom bug */
  /* clip the rectangle to make it look more like wings */
  clip-path: polygon(50% -4%, 100% 2%, 100% 100%, 50% 98%, 0 100%, 0 2%);
  
  .wing {
    background: rgba(55, 55, 55, 0.4) !important;
    background: linear-gradient(-90deg, 
      rgba(169,169,169,0.4) 0%,
      rgba(147,147,147,0.8) 6%,
      rgba(169,169,169,0.4) 100%
    ) !important;
     width: ${_ref => {
  let {
    showCabinTitles
  } = _ref;
  return showCabinTitles ? '85px' : '52px';
}}; 
  }
  .wing:nth-child(2) {
    background: linear-gradient(90deg, 
      rgba(169,169,169,0.4) 0%,
      rgba(147,147,147,0.8) 6%,
      rgba(169,169,169,0.4) 100%
    ) !important;
    right: 0;
  }
}
.jets-row {
  touch-action: unset;
}
/* targets popover/tooltip */
.jets-seat [data-state] {
  display: flex;
  flex: 1 1 auto;
  justify-content: center;
  transition: transform 0.2s;
}
.jets-available:hover  {
  filter: drop-shadow(#aaa 0px 2px 5px);
}

.jets-seat-r-nw ${OutOfPolicyIconWrapper},
.jets-seat-r-nw .jets-seat-number b {
  transform: rotate(20deg);
}

.jets-seat-r-nw45 ${OutOfPolicyIconWrapper},
.jets-seat-r-nw45 .jets-seat-number b {
  transform: rotate(45deg);
}

.jets-seat-r-ne ${OutOfPolicyIconWrapper},
.jets-seat-r-ne .jets-seat-number b {
  transform: rotate(-20deg);
}

.jets-seat-r-ne45 ${OutOfPolicyIconWrapper},
.jets-seat-r-ne45 .jets-seat-number b {
  transform: rotate(-45deg);
}

.jets-seat-r-s ${OutOfPolicyIconWrapper},
.jets-seat-r-s .jets-seat-number b {
  transform: rotate(-180deg);
}

.jets-seat-r-se ${OutOfPolicyIconWrapper},
.jets-seat-r-se .jets-seat-number b {
  transform: rotate(-160deg);
}

.jets-seat-r-sw ${OutOfPolicyIconWrapper},
.jets-seat-r-sw .jets-seat-number b {
  transform: rotate(160deg);
}
.jets-cabin-title-hl-right {
  border-color: #444 !important;
  border-left-width: 12px !important;
  transform: translateX(-32px);
}
.jets-cabin-title-label-right {
  background: #444 !important;
  color: white !important; 
  font-weight: bold;
  font-size: 14px;
  padding: 7px 12px 5px;
  border-radius: 4px 4px 0 0;
  margin: -14px -3px 0;
}
.jets-cabin-title-hl-left {
  display: none;
}
`;

const AmenityColor = styled.span`
  padding: 0 1px;
  display: flex;
`;
const Size14 = styled(AmenityColor)`
  font-size: 14px;
`;
const PowerSize = styled(Size14)``;
const Size16 = styled(AmenityColor)`
  font-size: 16px;
`;
const Size18 = styled(AmenityColor)`
  font-size: 18px;
`;
const StreamingSize = styled(Size18)``;
const PersonalVideo = styled(Size14)``;
const MaybeContainer = styled.span`
  position: relative;
`;
const MaybeDotIndicator = styled.span`
  background: ${getThemeProp('global.warning--color')};
  position: absolute;
  height: 8px;
  width: 8px;
  border-radius: 50%;
  border: 1px solid white;
  bottom: 0px;
  right: 0px;
  z-index: 1;
`;
const MaybeTitle = styled.b`
  color: ${getThemeProp('global.warning--color')};
`;

// eslint-disable-next-line react/prop-types
const Maybe = _ref => {
  let {
    children
  } = _ref;
  return /*#__PURE__*/React.createElement(MaybeContainer, null, /*#__PURE__*/React.createElement(MaybeDotIndicator, null), children);
};
var amenityIconByKey = attributeKey => {
  const map = [];
  map.LiveTV = {
    description: 'Live TV',
    icon: /*#__PURE__*/React.createElement(Size16, null, /*#__PURE__*/React.createElement(IconLiveTV, null))
  };
  map.MaybeLiveTV = {
    description: /*#__PURE__*/React.createElement(React.Fragment, null, "Live TV ", /*#__PURE__*/React.createElement(MaybeTitle, null, "(maybe)")),
    icon: /*#__PURE__*/React.createElement(Maybe, null, /*#__PURE__*/React.createElement(Size16, null, /*#__PURE__*/React.createElement(IconLiveTV, null)))
  };
  map.PersonalVideoOnDemand = {
    description: 'In Seat Video On Demand',
    icon: /*#__PURE__*/React.createElement(PersonalVideo, null, /*#__PURE__*/React.createElement(IconPersonalVideo, null))
  };
  map.PersonalVideoOnDemandOrStreaming = {
    description: /*#__PURE__*/React.createElement(React.Fragment, null, "Personal Video or Streaming Entertainment"),
    icon: /*#__PURE__*/React.createElement(PersonalVideo, null, /*#__PURE__*/React.createElement(IconPersonalVideo, null))
  };
  map.MaybePersonalVideoOnDemand = {
    description: /*#__PURE__*/React.createElement(React.Fragment, null, "In Seat Video On Demand ", /*#__PURE__*/React.createElement(MaybeTitle, null, "(maybe)")),
    icon: /*#__PURE__*/React.createElement(Maybe, null, /*#__PURE__*/React.createElement(PersonalVideo, null, /*#__PURE__*/React.createElement(IconPersonalVideo, null)))
  };
  map.Streaming = {
    description: 'Streaming Video',
    icon: /*#__PURE__*/React.createElement(StreamingSize, null, /*#__PURE__*/React.createElement(IconStreamingVideo, null))
  };
  map.MaybeStreaming = {
    description: /*#__PURE__*/React.createElement(React.Fragment, null, "Streaming Video ", /*#__PURE__*/React.createElement(MaybeTitle, null, "(maybe)")),
    icon: /*#__PURE__*/React.createElement(Maybe, null, /*#__PURE__*/React.createElement(StreamingSize, null, /*#__PURE__*/React.createElement(IconStreamingVideo, null)))
  };
  map.PersonalVideo = {
    description: 'In Seat Video',
    icon: /*#__PURE__*/React.createElement(PersonalVideo, null, /*#__PURE__*/React.createElement(IconPersonalVideo, null))
  };
  map.PersonalVideoOrStreaming = {
    description: /*#__PURE__*/React.createElement(React.Fragment, null, "Personal Video or Streaming Entertainment"),
    icon: /*#__PURE__*/React.createElement(PersonalVideo, null, /*#__PURE__*/React.createElement(IconPersonalVideo, null))
  };
  map.MaybePersonalVideo = {
    description: /*#__PURE__*/React.createElement(React.Fragment, null, "In Seat Video ", /*#__PURE__*/React.createElement(MaybeTitle, null, "(maybe)")),
    icon: /*#__PURE__*/React.createElement(Maybe, null, /*#__PURE__*/React.createElement(PersonalVideo, null, /*#__PURE__*/React.createElement(IconPersonalVideo, null)))
  };
  map.Tablet = {
    description: 'Handheld Video Player ,',
    icon: /*#__PURE__*/React.createElement(StreamingSize, null, /*#__PURE__*/React.createElement(IconStreamingVideo, null))
  };
  map.MaybeTablet = {
    description: /*#__PURE__*/React.createElement(React.Fragment, null, "Handheld Video Player ", /*#__PURE__*/React.createElement(MaybeTitle, null, "(maybe)")),
    icon: /*#__PURE__*/React.createElement(Maybe, null, /*#__PURE__*/React.createElement(StreamingSize, null, /*#__PURE__*/React.createElement(IconStreamingVideo, null)))
  };
  map.Overhead = {
    description: 'Overhead Movies',
    icon: /*#__PURE__*/React.createElement(StreamingSize, null, /*#__PURE__*/React.createElement(IconStreamingVideo, null))
  };
  map.MaybeOverhead = {
    description: /*#__PURE__*/React.createElement(React.Fragment, null, "Overhead Movies ", /*#__PURE__*/React.createElement(MaybeTitle, null, "(maybe)")),
    icon: /*#__PURE__*/React.createElement(StreamingSize, null, /*#__PURE__*/React.createElement(IconStreamingVideo, null))
  };
  map.USB = {
    description: 'USB',
    icon: /*#__PURE__*/React.createElement(Size16, null, /*#__PURE__*/React.createElement(IconUSB, null))
  };
  map.MaybeUSB = {
    description: /*#__PURE__*/React.createElement(React.Fragment, null, "USB ", /*#__PURE__*/React.createElement(MaybeTitle, null, "(maybe)")),
    icon: /*#__PURE__*/React.createElement(Maybe, null, /*#__PURE__*/React.createElement(Size16, null, /*#__PURE__*/React.createElement(IconUSB, null)))
  };
  map.PowerAndUSB = {
    description: 'Power & USB',
    icon: /*#__PURE__*/React.createElement(PowerSize, null, /*#__PURE__*/React.createElement(IconPower, null))
  };
  map.MaybePowerAndUSB = {
    description: /*#__PURE__*/React.createElement(React.Fragment, null, "Power & USB ", /*#__PURE__*/React.createElement(MaybeTitle, null, "(maybe)")),
    icon: /*#__PURE__*/React.createElement(Maybe, null, /*#__PURE__*/React.createElement(PowerSize, null, /*#__PURE__*/React.createElement(IconPower, null)))
  };
  map.Power = {
    description: 'Power Port',
    icon: /*#__PURE__*/React.createElement(PowerSize, null, /*#__PURE__*/React.createElement(IconPower, null))
  };
  map.MaybePower = {
    description: /*#__PURE__*/React.createElement(React.Fragment, null, "Power Port ", /*#__PURE__*/React.createElement(MaybeTitle, null, "(maybe)")),
    icon: /*#__PURE__*/React.createElement(Maybe, null, /*#__PURE__*/React.createElement(PowerSize, null, /*#__PURE__*/React.createElement(IconPower, null)))
  };
  map.PowerWithAdapter = {
    description: 'Power with Adapter',
    icon: /*#__PURE__*/React.createElement(PowerSize, null, /*#__PURE__*/React.createElement(IconPower, null))
  };
  map.MaybePowerWithAdapter = {
    description: /*#__PURE__*/React.createElement(React.Fragment, null, "Power with Adapter ", /*#__PURE__*/React.createElement(MaybeTitle, null, "(maybe)")),
    icon: /*#__PURE__*/React.createElement(Maybe, null, /*#__PURE__*/React.createElement(PowerSize, null, /*#__PURE__*/React.createElement(IconPower, null)))
  };
  return map[attributeKey];
};

const PerkDisadvantageText = styled.p`
  padding: 0;
  margin: 0;
  line-height: unset;

  &::first-letter {
    text-transform: uppercase;
  }
`;
const List = styled.ul`
  all: unset;
`;
const PerksRegion = styled.div`
  color: ${getThemeProp('global.success--color')};
  margin: 6px 0;
  font-weight: bold;
`;
const DisadvantageRegion = styled(PerksRegion)`
  color: ${getThemeProp('global.warning--color')};
`;
const AmenityItem = styled(Flex)`
  text-transform: capitalize;
  line-height: 1.8;
  align-items: center;
`;
const IGNORED_AMENITIES = [
/* 
We intentionally leave off WiFi as this is an aircraft-level amenity for which we have 
more reliable data that we show on the results. */
'wifi'];
const AMENITIES = {
  audioVideo: /*#__PURE__*/React.createElement(IconPersonalVideo, null),
  power: /*#__PURE__*/React.createElement(IconPower, null)
};
const PERKS = {
  extraLegroom: 'Extra legroom.',
  exitRow: 'Exit row.'
};
const DISADVANTAGES = {
  limitedRecline: 'Restricted recline.',
  doNotRecline: 'Restricted recline.',
  restrictedLegroom: 'Constrained legroom.',
  nearGalley: 'Close to galleys.',
  nearLavatory: 'Close to restrooms.',
  noFloorStorage: 'No underseat storage.',
  trayTableInArmrest: 'Tray table in the armrest.',
  wingInWindow: 'Wing from window view.',
  noWindow: 'Partial or missing window view.',
  noOverheadStorage: 'Limited storage space.',
  nearStairs: 'Near stairs.',
  misalignedWindow: 'Partial or missing window view.',
  limitedOverheadStorage: 'Limited storage space.',
  getColdByExit: 'Near exit, drafts and chilly.',
  narrower: 'Narrower seat'
};
const SeatPerksAndDisadvantages = _ref => {
  let {
    items,
    suppressAmenities = false
  } = _ref;
  const amenitiesList = [];
  const perksList = [];
  const disadvantagesList = [];

  // remove ignored items
  const filteredItems = items.filter(item => !IGNORED_AMENITIES.includes(item.key));
  filteredItems.forEach(_ref2 => {
    let {
      key,
      value
    } = _ref2;
    if (AMENITIES[key] && !suppressAmenities) {
      amenitiesList.push({
        icon: AMENITIES[key],
        value
      });
    } else if (PERKS[key]) {
      perksList.push(PERKS[key]);
    } else if (DISADVANTAGES[key]) {
      disadvantagesList.push(DISADVANTAGES[key]);
    } else ;
  });
  return /*#__PURE__*/React.createElement(React.Fragment, null, amenitiesList.length > 0 && /*#__PURE__*/React.createElement(List, null, amenitiesList.map(_ref3 => {
    let {
      icon,
      value
    } = _ref3;
    return /*#__PURE__*/React.createElement(AmenityItem, {
      key: value,
      py: "4px"
    }, /*#__PURE__*/React.createElement(IconFlex, {
      size: 14,
      pl: 2,
      pr: "12px",
      inheritColor: true
    }, icon), /*#__PURE__*/React.createElement(Text, {
      size: 12
    }, value));
  })), perksList.length > 0 && /*#__PURE__*/React.createElement(PerksRegion, null, /*#__PURE__*/React.createElement(PerkDisadvantageText, null, perksList.map(s => s.replace('.', '').toLowerCase()).join(', '), ".")), disadvantagesList.length > 0 && /*#__PURE__*/React.createElement(DisadvantageRegion, null, /*#__PURE__*/React.createElement(PerkDisadvantageText, null, disadvantagesList.map(s => s.replace('.', '').toLowerCase()).join(', '), ".")));
};
SeatPerksAndDisadvantages.propTypes = {
  suppressAmenities: PropTypes.bool,
  items: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string
  }))
};

/* eslint-disable max-len */
const SEAT_STATUS = {
  available: 'Y',
  occupied: 'N',
  blocked: 'R',
  nonExistent: 'X',
  galley: 'G',
  lavatory: 'L',
  closet: 'C'
};
const SEAT_MEASUREMENTS = {
  pitch: '<svg width="74" height="51" viewBox="0 0 74 51" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M2.98892 0.152473C1.60004 0.510215 0.442636 1.49927 0.253243 3.2038V3.30902C-0.503624 20.2124 0.272842 21.9085 4.82805 31.8585L4.84076 31.8863L4.99918 32.2325L4.99924 32.2327L4.99927 32.2327C5.87007 34.1358 6.88816 36.3607 8.01834 38.978C8.334 39.7355 8.64965 40.2616 9.36514 40.6615C9.9754 40.9982 10.754 41.1455 12.0166 41.1665C13.2619 41.187 14.4597 41.2 15.6234 41.2077L17.9559 48.0057H11.2591C10.5857 48.0057 10.0175 48.5528 10.0175 49.2473C10.0175 49.9207 10.5646 50.4888 11.2591 50.4888H19.5541C19.6426 50.4987 19.7329 50.499 19.8235 50.4888H31.2295C31.9029 50.4888 32.471 49.9417 32.471 49.2473C32.471 48.5739 31.9239 48.0057 31.2295 48.0057H20.5872L18.2578 41.2168C20.2165 41.2179 22.1025 41.2077 23.9874 41.1974H23.9883H23.9892L23.992 41.1974L23.9947 41.1974C24.6238 41.194 25.2528 41.1906 25.8844 41.1876C26.7241 41.1876 27.5679 41.1835 28.5151 41.179H28.5156H28.5162C29.7287 41.1731 31.1108 41.1665 32.8709 41.1665C34.1335 41.1876 34.9752 40.3037 35.3961 39.1253C35.5434 38.7044 35.6276 38.2415 35.6486 37.7785C35.6697 37.3155 35.6276 36.8315 35.5224 36.3896C35.2067 35.148 34.4071 34.1379 32.9761 34.0117C28.6832 33.6539 25.8213 33.4014 22.9804 33.1489C21.6481 33.0323 20.3191 32.9124 19.0403 32.7971L19.0374 32.7968L19.0368 32.7968L19.0366 32.7968C17.085 32.6207 15.2506 32.4553 13.7001 32.3282C8.18669 20.3544 7.00825 17.5976 7.9973 3.43528C8.10252 1.96223 7.23973 0.910044 6.04024 0.383953C5.57728 0.173516 5.07224 0.0472546 4.54614 0.00516733C4.02005 -0.0158763 3.49396 0.026211 2.98892 0.152473ZM16.7077 38.6924C19.9006 38.7099 22.8527 38.691 25.8633 38.6623C28.4728 38.6413 31.1032 38.6202 32.8498 38.6202H32.8709C32.8709 38.6202 32.934 38.4729 33.0182 38.2625C33.0813 38.0731 33.1234 37.8416 33.1444 37.6101C33.1655 37.3787 33.1444 37.1472 33.0813 36.9367C33.0182 36.6632 32.913 36.4738 32.7446 36.4527C32.2021 36.4054 31.6163 36.3545 30.9963 36.3006L30.9954 36.3005C28.5853 36.0911 25.6566 35.8367 22.7278 35.5689L22.7265 35.5688L22.7256 35.5687L22.7244 35.5686C19.8846 35.3162 17.0443 35.0637 12.7321 34.7061L12.0166 34.643L11.722 33.9906L11.6378 33.8223C5.70355 20.9646 4.44093 18.2079 5.49311 3.24589C5.51415 2.97232 5.30371 2.76188 5.0091 2.63562C4.81971 2.55145 4.58823 2.48832 4.33571 2.46727C4.08319 2.44623 3.83066 2.46727 3.59918 2.5304L3.62022 2.55145C3.17831 2.67771 2.79952 2.97232 2.73639 3.45632C2.02158 19.7078 2.75606 21.3194 7.08023 30.8073L7.09242 30.8341C7.9973 32.7911 9.04948 35.0849 10.2911 37.9679C10.3963 38.2415 10.5015 38.4098 10.5857 38.4519C10.7961 38.5781 11.217 38.6202 12.0377 38.6413C13.5277 38.6658 14.948 38.6814 16.322 38.6901C16.4521 38.67 16.5821 38.6715 16.7077 38.6924ZM40.9889 0.152473C39.6 0.510215 38.4426 1.49927 38.2532 3.2038V3.30902C37.4964 20.2124 38.2728 21.9085 42.828 31.8585L42.8408 31.8863L42.9991 32.2323C43.8699 34.1355 44.8881 36.3605 46.0183 38.978C46.334 39.7355 46.6497 40.2616 47.3651 40.6615C47.9754 40.9982 48.754 41.1455 50.0166 41.1665C51.2619 41.187 52.4597 41.2 53.6234 41.2077L55.9559 48.0057H49.2591C48.5857 48.0057 48.0175 48.5528 48.0175 49.2473C48.0175 49.9207 48.5646 50.4888 49.2591 50.4888H57.5541C57.6426 50.4987 57.7329 50.499 57.8235 50.4888H69.2295C69.9029 50.4888 70.471 49.9417 70.471 49.2473C70.471 48.5739 69.9239 48.0057 69.2295 48.0057H58.5872L56.2578 41.2168C58.2165 41.2179 60.1025 41.2077 61.9874 41.1974H61.9883H61.9892C62.6201 41.194 63.251 41.1906 63.8844 41.1876C64.7241 41.1876 65.5679 41.1835 66.5151 41.179H66.5156H66.5162C67.7288 41.1731 69.1108 41.1665 70.8709 41.1665C72.1335 41.1876 72.9752 40.3037 73.3961 39.1253C73.5434 38.7044 73.6276 38.2415 73.6486 37.7785C73.6697 37.3155 73.6276 36.8315 73.5224 36.3896C73.2067 35.148 72.4071 34.1379 70.9761 34.0117C66.6832 33.6539 63.8213 33.4014 60.9804 33.1489C59.6486 33.0324 58.3201 32.9125 57.0417 32.7972L57.0376 32.7969L57.0368 32.7968L57.0366 32.7968L57.0361 32.7967C55.0847 32.6207 53.2505 32.4553 51.7001 32.3282C46.1867 20.3544 45.0083 17.5976 45.9973 3.43528C46.1025 1.96223 45.2397 0.910044 44.0402 0.383953C43.5773 0.173516 43.0722 0.0472546 42.5461 0.00516733C42.0201 -0.0158763 41.494 0.026211 40.9889 0.152473ZM54.7077 38.6924C57.9006 38.7099 60.8527 38.691 63.8633 38.6623C66.4728 38.6413 69.1032 38.6202 70.8498 38.6202H70.8709C70.8709 38.6202 70.934 38.4729 71.0182 38.2625C71.0813 38.0731 71.1234 37.8416 71.1444 37.6101C71.1655 37.3787 71.1444 37.1472 71.0813 36.9367C71.0182 36.6632 70.913 36.4738 70.7446 36.4527C70.2021 36.4054 69.6163 36.3545 68.9963 36.3006L68.9954 36.3005C66.5853 36.0911 63.6566 35.8367 60.7278 35.5689L60.7265 35.5688C57.886 35.3163 55.0454 35.0638 50.7321 34.7061L50.0166 34.643L49.722 33.9906L49.6378 33.8223C43.7035 20.9646 42.4409 18.2079 43.4931 3.24589C43.5142 2.97232 43.3037 2.76188 43.0091 2.63562C42.8197 2.55145 42.5882 2.48832 42.3357 2.46727C42.0832 2.44623 41.8307 2.46727 41.5992 2.5304L41.6202 2.55145C41.1783 2.67771 40.7995 2.97232 40.7364 3.45632C40.0216 19.7078 40.7561 21.3194 45.0802 30.8073L45.0924 30.8341C45.9973 32.7911 47.0495 35.0849 48.2911 37.9679C48.3963 38.2415 48.5015 38.4098 48.5857 38.4519C48.7961 38.5781 49.217 38.6202 50.0377 38.6413C51.5277 38.6658 52.948 38.6814 54.322 38.6901C54.4521 38.67 54.5821 38.6715 54.7077 38.6924Z"></path></svg>',
  width: '<svg width="35" height="50" viewBox="0 0 35 50" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M31.1151 33.3111C32.0089 33.4715 32.8177 33.9081 33.4335 34.5238C34.2198 35.31 34.7141 36.4106 34.7141 37.6011C34.7141 38.7916 34.2198 39.8922 33.4335 40.6784C32.6472 41.4645 31.5464 41.9587 30.3557 41.9587H23.2082L23.1928 47.4843H27.8396C28.5361 47.4843 29.0977 48.0458 29.0977 48.7421C29.0977 49.4385 28.5361 50 27.8396 50H7.86769C7.17126 50 6.60962 49.4385 6.60962 48.7421C6.60962 48.0458 7.17126 47.4843 7.86769 47.4843H12.4992L12.5146 41.9587H5.35149C4.16082 41.9587 3.06 41.4645 2.2737 40.6784C1.48741 39.8922 0.993164 38.7916 0.993164 37.6011C0.993164 36.4106 1.48741 35.31 2.2737 34.5238C2.88437 33.9133 3.68474 33.4788 4.56958 33.3152L4.82779 23.4241C4.72932 23.4271 4.63334 23.4314 4.51247 23.4369C4.3114 23.446 4.04146 23.4581 3.57663 23.4727C2.90266 23.4951 2.31855 22.956 2.29609 22.2597C2.27362 21.5859 2.8128 21.0019 3.50923 20.9794C3.98488 20.965 4.26402 20.9529 4.47273 20.9438C4.63574 20.9368 4.75578 20.9316 4.89293 20.9286L5.32902 4.22282C5.35149 3.05481 5.84573 1.9991 6.60956 1.2354C7.37339 0.471698 8.42928 0 9.59749 0H26.0873C27.2555 0 28.3114 0.471698 29.0752 1.2354C29.839 1.9991 30.3108 3.05481 30.3557 4.22282L30.7921 20.9384C30.8322 20.94 30.8743 20.9419 30.9198 20.9438C31.1286 20.9529 31.4077 20.965 31.8833 20.9794C32.5798 21.0019 33.1189 21.5859 33.0965 22.2597C33.074 22.956 32.4899 23.4951 31.8159 23.4727C31.3511 23.4581 31.0812 23.446 30.8801 23.4369C30.8724 23.4365 30.8648 23.4362 30.8573 23.4359L31.1151 33.3111ZM20.6991 47.4843L20.7145 41.9587H15.0083L14.9929 47.4843H20.6991ZM27.8171 4.26775L28.5809 33.221H7.05887L7.82271 4.26775C7.82271 3.77359 8.0249 3.32435 8.36188 2.98742C8.6764 2.67296 9.10325 2.4708 9.57502 2.4708H26.0648C26.5366 2.4708 26.9634 2.67296 27.2779 2.98742C27.5925 3.32435 27.7946 3.77359 27.8171 4.26775ZM30.3557 35.7368H5.35149C4.83479 35.7368 4.38547 35.9389 4.04849 36.2759C3.7115 36.6128 3.50931 37.0845 3.50931 37.5787C3.50931 38.0953 3.7115 38.5445 4.04849 38.8814C4.38547 39.2184 4.85725 39.4205 5.35149 39.4205H30.3557C30.8724 39.4205 31.3218 39.2184 31.6587 38.8814C31.9957 38.5445 32.1979 38.0728 32.1979 37.5787C32.1979 37.062 31.9957 36.6128 31.6587 36.2759C31.3218 35.9389 30.85 35.7368 30.3557 35.7368Z"></path></svg>',
  recline: '<svg width="48" height="51" viewBox="0 0 48 51" xmlns="http://www.w3.org/2000/svg"><g transform="scale(-1, 1) translate(-48,0)"><path d="M19.3954 30.8342C19.7952 31.1919 20.4265 30.8973 20.4265 30.3502C20.4265 30.1818 20.3424 30.0345 20.2161 29.9082L20.1319 29.8241L19.2902 29.1507L19.2691 29.1296C18.8272 28.7298 18.1538 29.1086 18.259 29.6978C18.2801 29.8451 18.3432 29.9503 18.4484 30.0345L18.4695 30.0555L19.3112 30.7289L19.3954 30.8131V30.8342Z"></path><path fill-rule="evenodd" clip-rule="evenodd" d="M14.5973 0.152473C13.2084 0.510215 12.051 1.49927 11.8616 3.2038V3.30902C11.4284 12.9854 11.4976 17.6783 12.4905 21.7889L11.967 21.091L11.946 21.0489C11.8197 20.8384 11.5672 20.7543 11.3357 20.7753C10.8938 20.8384 10.6833 21.3435 10.9148 21.7223L10.9359 21.7643L11.6303 22.6903L11.6724 22.7534L11.6934 22.7955C11.9769 23.1853 12.5288 23.0977 12.734 22.7212C12.8634 23.1823 13.0052 23.6402 13.1601 24.1013C13.1537 24.128 13.1488 24.1557 13.1454 24.1844C13.1244 24.3527 13.1875 24.5211 13.2927 24.6473L13.3138 24.6894L13.4099 24.8175C14.1463 26.8561 15.1378 29.0219 16.4364 31.8585L16.4492 31.8863C16.5014 32.0005 16.5542 32.1159 16.6075 32.2325C17.1074 33.3248 17.6558 34.5232 18.246 35.841C18.0278 35.9337 17.8592 36.149 17.8592 36.4318C17.8592 36.6001 17.9434 36.7474 18.0697 36.8737L18.8904 37.6944L18.9535 37.7365C19.0105 37.7891 19.073 37.828 19.1381 37.8543C19.2985 38.2206 19.4614 38.5951 19.6267 38.978C19.9424 39.7355 20.2581 40.2616 20.9735 40.6615C21.5838 40.9982 22.3624 41.1455 23.625 41.1665C24.8703 41.187 26.0681 41.2 27.2319 41.2077L29.5644 48.0056H22.8676C22.1942 48.0056 21.626 48.5527 21.626 49.2472C21.626 49.9206 22.1731 50.4888 22.8676 50.4888H31.1617C31.2508 50.4988 31.3418 50.499 31.433 50.4888H42.838C43.5114 50.4888 44.0795 49.9416 44.0795 49.2472C44.0795 48.5738 43.5324 48.0056 42.838 48.0056H32.1957L29.8663 41.2168C31.8256 41.2179 33.7121 41.2077 35.5976 41.1974C36.2285 41.194 36.8594 41.1906 37.4928 41.1876C38.3328 41.1876 39.1769 41.1835 40.1246 41.179C41.3371 41.1731 42.7192 41.1665 44.4793 41.1665C45.7419 41.1876 46.5836 40.3037 47.0045 39.1253C47.1518 38.7044 47.236 38.2415 47.257 37.7785C47.2781 37.3155 47.236 36.8315 47.1308 36.3896C46.8151 35.148 46.0155 34.1379 44.5845 34.0117C40.2916 33.6539 37.4297 33.4014 34.5888 33.1489C33.2553 33.0322 31.925 32.9122 30.6452 32.7968C28.6936 32.6208 26.859 32.4553 25.3085 32.3282C19.7951 20.3544 18.6166 17.5976 19.6057 3.43528C19.7109 1.96223 18.8481 0.910044 17.6486 0.383953C17.1857 0.173516 16.6806 0.0472546 16.1545 0.00516733C15.6285 -0.0158763 15.1024 0.026211 14.5973 0.152473ZM28.3161 38.6924C31.509 38.7099 34.4611 38.691 37.4717 38.6623C40.0812 38.6413 42.7116 38.6202 44.4582 38.6202H44.4793C44.4793 38.6202 44.5424 38.4729 44.6266 38.2625C44.6897 38.0731 44.7318 37.8416 44.7528 37.6101C44.7739 37.3787 44.7528 37.1472 44.6897 36.9367C44.6266 36.6632 44.5214 36.4738 44.353 36.4527C43.8105 36.4054 43.2248 36.3545 42.6047 36.3006C40.1945 36.0912 37.2654 35.8367 34.3362 35.5689L34.335 35.5688C31.4945 35.3163 28.6538 35.0638 24.3405 34.7061L23.625 34.643L23.3304 33.9906L23.2462 33.8223C23.1555 33.6256 23.0658 33.4313 22.9772 33.2392C22.7687 33.4555 22.4052 33.5083 22.1311 33.2752V33.2963L22.0469 33.2331L21.5419 32.8123L21.2052 32.4756C21.0789 32.3493 21.0158 32.223 20.9947 32.0547C20.9526 31.5075 21.605 31.1919 22.0048 31.5496L22.3415 31.8863L22.3617 31.9031C17.1874 20.6464 16.1038 17.4332 17.1015 3.24589C17.1226 2.97232 16.9121 2.76188 16.6175 2.63562C16.4281 2.55145 16.1966 2.48832 15.9441 2.46727C15.6916 2.44623 15.4391 2.46727 15.2076 2.5304L15.2286 2.55145C14.7867 2.67771 14.4079 2.97232 14.3448 3.45632C13.7423 17.155 14.1695 20.4518 16.9083 26.8319L17.5225 27.4461C17.7771 27.7007 17.767 28.0756 17.5559 28.3069C17.9026 29.0826 18.2795 29.9097 18.6886 30.8073L18.7008 30.8341C19.6057 32.7912 20.6579 35.0849 21.8995 37.9679C22.0047 38.2415 22.1099 38.4098 22.1941 38.4519C22.4045 38.5781 22.8254 38.6202 23.6461 38.6413C25.1362 38.6658 26.5566 38.6814 27.9307 38.6901C28.0607 38.6701 28.1906 38.6716 28.3161 38.6924Z"></path><path d="M0.140476 10.527L0.132009 10.5185L0.182563 10.6533L0.0983887 10.485V9.05399C0.140476 8.88564 0.245694 8.75938 0.371956 8.67521C0.834916 8.38059 1.42414 8.80147 1.29788 9.32756V10.1062L1.38205 10.3587C1.5504 10.7796 1.2137 11.2215 0.771785 11.2004C0.540305 11.1794 0.329869 11.0321 0.245694 10.8006L0.22465 10.7375L0.140476 10.527Z"></path><path d="M2.26588 12.6525C2.05545 12.1264 1.31892 12.1474 1.12953 12.6525C1.0664 12.7998 1.0664 12.9471 1.12953 13.0944L1.15057 13.1575L1.48727 13.9151L1.59249 14.2307C1.80292 14.7358 2.51841 14.7358 2.72884 14.2307C2.79197 14.0834 2.79197 13.9151 2.72884 13.7678L2.56049 13.2627L2.30797 12.6945L2.28693 12.6314L2.26588 12.6525Z"></path><path d="M3.63372 16.0405C3.42328 15.5354 2.7078 15.5354 2.49736 16.0405C2.43423 16.1878 2.43423 16.3561 2.49736 16.5034V16.5245L2.68676 17.0295L2.93928 17.6188V17.6398C3.17076 18.1659 3.90729 18.1238 4.07564 17.5977C4.11772 17.4504 4.11772 17.3031 4.05459 17.1558V17.1347L3.73894 16.3982L3.61268 16.0615V16.0405H3.63372Z"></path><path d="M5.08573 19.3864C4.83321 18.8393 4.05459 18.9445 3.92833 19.5337C3.90729 19.66 3.92833 19.7862 3.97042 19.9125V19.9336L4.51755 21.0278C4.62277 21.2383 4.85425 21.3856 5.08573 21.3856C5.52765 21.3645 5.80121 20.9016 5.61182 20.5017L5.06469 19.4075V19.3864H5.08573Z"></path><path d="M5.56973 22.7534C5.5066 22.9217 5.52765 23.0901 5.61182 23.2374L6.24313 24.2896L6.28522 24.3527C6.41148 24.5421 6.62192 24.6684 6.85339 24.6473C7.31636 24.6263 7.56888 24.1212 7.3374 23.7214L7.29531 23.6583L6.664 22.6061L6.68505 22.6482C6.43252 22.1852 5.73808 22.2273 5.56973 22.7534Z"></path><path d="M8.57897 25.6995C8.26332 25.2576 7.54784 25.4259 7.46366 25.9731C7.44262 26.1204 7.4847 26.2887 7.56888 26.415L8.32645 27.4251L8.34749 27.4461C8.68419 27.867 9.37863 27.6776 9.44176 27.1305C9.46281 26.9621 9.42072 26.8148 9.3155 26.6886L8.55793 25.6785L8.57897 25.6995Z"></path><path d="M10.8517 28.4773C10.7044 28.3089 10.4729 28.2458 10.2625 28.3089C9.7995 28.4141 9.63116 28.9823 9.96785 29.319L9.9889 29.3401L10.3677 29.8451L10.8306 30.3081C11.2094 30.6868 11.8828 30.4133 11.8828 29.8661C11.8828 29.6978 11.8197 29.5505 11.6934 29.4453L11.6513 29.4032L11.5251 29.2348L10.9359 28.6456L10.8517 28.4773Z"></path><path d="M13.4401 31.0235C13.0613 30.6658 12.451 30.9183 12.4089 31.4444C12.4089 31.6338 12.472 31.8022 12.5983 31.9284L13.4401 32.7702C13.8399 33.1279 14.4712 32.8333 14.4712 32.3072C14.4712 32.1389 14.387 31.9705 14.2608 31.8653L13.419 31.0235H13.4401Z"></path><path d="M16.1547 33.4857C15.7549 33.1279 15.1446 33.4225 15.1446 33.9486C15.1446 34.117 15.2288 34.2853 15.355 34.4116L16.2178 35.2744C16.3651 35.4217 16.5756 35.4638 16.786 35.4217C17.27 35.2954 17.4173 34.7062 17.0385 34.3695L16.1547 33.4857Z"></path><path d="M9.67324 19.6389C9.94681 20.1019 10.6202 20.0388 10.7886 19.5127C10.8306 19.3443 10.8306 19.176 10.7465 19.0287L10.3887 18.3132L10.3046 18.1028L10.2625 18.0186C10.031 17.5556 9.33654 17.5977 9.14715 18.0817C9.08402 18.2501 9.08402 18.4184 9.16819 18.5868L9.21028 18.6709L9.54698 19.5127L9.6522 19.7231L9.67324 19.6389Z"></path><path d="M8.0108 16.314C8.11601 16.5455 8.32645 16.6718 8.57897 16.6718C9.02089 16.6507 9.29446 16.2088 9.12611 15.809L8.78941 14.9673L8.74732 14.841L8.70523 14.7568C8.4948 14.2728 7.8214 14.2518 7.58992 14.7358C7.52679 14.9041 7.50575 15.0725 7.58992 15.2408L7.63201 15.325L7.80036 15.83L8.0108 16.3561V16.314Z"></path><path d="M6.57983 12.905C6.79026 13.4311 7.54784 13.41 7.73723 12.8839C7.77931 12.7366 7.77931 12.5893 7.73723 12.4631L7.71618 12.442L7.54784 11.937L7.4847 11.8107L7.29531 11.3688V11.3477C7.08488 10.8006 6.30626 10.8217 6.13791 11.3898C6.09582 11.5161 6.09582 11.6424 6.15896 11.7897V11.8107L6.47461 12.5472L6.664 12.926L6.68505 12.9471L6.57983 12.905Z"></path><path d="M5.27512 9.45382C5.48556 10.001 6.26417 9.97991 6.43252 9.43278C6.47461 9.30651 6.47461 9.18025 6.43252 9.05399L6.41148 8.99086L6.28522 8.69625L6.03269 8.02285L5.94852 7.8545L5.92748 7.83346C5.696 7.32841 4.95947 7.3705 4.79112 7.89659C4.74903 8.0439 4.74903 8.1912 4.81216 8.33851V8.44372L5.12782 9.28547L5.19095 9.43278L5.21199 9.49591L5.27512 9.45382Z"></path><path d="M3.44433 7.66511C3.97042 7.62302 4.2019 6.97067 3.82311 6.61293C3.69685 6.48667 3.5285 6.44458 3.36015 6.44458H2.28693L1.65562 6.76023H1.63457C1.40309 6.86545 1.27683 7.09693 1.27683 7.32841C1.29788 7.77033 1.73979 8.0439 2.13962 7.8545H3.00241L3.44433 7.66511Z"></path></g></svg>'
};

const params = new URLSearchParams(document.location.search);
const devModeEnabled = (params === null || params === void 0 ? void 0 : params.get('devMode')) === 'true';
const SeatTitleRegion = styled.div`
  padding: 8px 12px;
  border-bottom: 2px solid ${getThemeProp('global.greyPalette.300')};

  ${_ref => {
  let {
    hideBorder
  } = _ref;
  return hideBorder && css`
      border-bottom: 0;
    `;
}};

  .isPopover & {
    padding: 0 0 4px;
  }
`;
const SeatTitle = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-radius: 4px 4px 0 0;
  font-size: 18px;
  gap: 16px;

  code {
    font-weight: normal;
  }
`;
const SeatPrice$1 = styled.div`
  background: #424242;
  color: white;
  border-radius: 4px;
  padding: 2px 8px;
`;
const SeatMeasurement = styled.div`
  display: flex;
  gap: 8px;
  padding-top: 8px;
`;
const SeatMeasurementLabel = styled(Text)`
  opacity: 0.6;
`;
const SetMetricMeasurements = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-bottom: 8px;

  .isPopover & {
    margin-bottom: 0;
  }
`;
const SeatMeasurementRegion = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 0 auto;
  background-color: ${getThemeProp('global.greyPalette.200')};
  padding: 8px 10px;
  border-radius: 4px;
  margin: 8px 0;
  outline: 1px solid ${getThemeProp('global.greyPalette.300')};

  .isPopover & {
    margin-bottom: 0;
  }
`;
const SeatMeasurementIcon = styled(Flex)`
  padding: 0 6px 0 0px;

  svg {
    opacity: 0.5;
    height: 14px;
    width: 100%;
  }
`;
const ContentPadding = styled.div`
  padding: 12px 12px 4px;

  .isPopover & {
    padding: 8px 0 0;
  }
`;
const TheirData = styled.div`
  --seatdata-color: maroon;
  border-left: 4px solid var(--seatdata-color);
  padding: 4px 0px 4px 8px;
  margin: 8px;
  position: relative;
  border-radius: 8px 0 0 8px;

  &::before {
    content: 'SeatMaps.com';
    border-radius: 0 0 4px 4px;
    font-size: 10px;
    color: var(--seatdata-color);
    padding: 8px 12px 0;
    transform: rotate(90deg) translateY(21px);
    left: -26px;
    width: 80px;
    text-align: center;
    top: calc(50% - 12px);
    position: absolute;
  }
`;
const OurData = styled(TheirData)`
  --seatdata-color: #0776fc;
  &::before {
    content: 'Ours';
  }
`;
const OutOfPolicyBanner = _ref2 => {
  let {
    children
  } = _ref2;
  return /*#__PURE__*/React.createElement(Notice, {
    mx: "12px",
    mt: "2px",
    px: "6px",
    compact: true,
    kind: "warning"
  }, children);
};
OutOfPolicyBanner.propTypes = {
  children: PropTypes.node
};
const generateAmtravSeatMeasurements = (pitch, width) => {
  const amtravSeatMeasurements = [];
  if (pitch) {
    amtravSeatMeasurements.push({
      uniqId: 'seatPitch',
      title: 'Pitch',
      icon: SEAT_MEASUREMENTS.pitch,
      value: pitch,
      key: 'seatPitch'
    });
  }
  if (width) {
    amtravSeatMeasurements.push({
      uniqId: 'seatWidth',
      title: 'Width',
      icon: SEAT_MEASUREMENTS.width,
      value: width,
      key: 'seatWidth'
    });
  }
  return amtravSeatMeasurements;
};
const SeatTooltipContent = _ref3 => {
  let {
    customSeatData,
    data,
    showSelectButton,
    onSelectClick,
    isSeatSelected
  } = _ref3;
  const {
    seatPrice = 0,
    cabinBrandName = '',
    outOfPolicyReason = '',
    isEliteSeat,
    seatWidth,
    seatPitch,
    amenities = []
  } = customSeatData !== null && customSeatData !== void 0 ? customSeatData : {};
  const {
    rowName,
    number,
    measurements,
    features
  } = data;
  const seatAmenities = amenities.map(amenity => {
    return amenityIconByKey(amenity.attribute);
  }).filter(a => a);
  const labelNameOfCabin = cabinBrandName || rowName;
  const hasOurSeatAmenityData = (seatAmenities === null || seatAmenities === void 0 ? void 0 : seatAmenities.length) > 0;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(SeatTitleRegion, {
    hideBorder: outOfPolicyReason || isEliteSeat
  }, /*#__PURE__*/React.createElement(SeatTitle, null, /*#__PURE__*/React.createElement("b", null, labelNameOfCabin, " ", /*#__PURE__*/React.createElement("code", null, number)), seatPrice && seatPrice > 0 ? /*#__PURE__*/React.createElement(SeatPrice$1, null, " $", seatPrice.toFixed(2)) : null)), outOfPolicyReason ? /*#__PURE__*/React.createElement(OutOfPolicyBanner, null, outOfPolicyReason) : null, isEliteSeat ? /*#__PURE__*/React.createElement(Notice, {
    mx: SPACE.size12,
    mt: outOfPolicyReason ? SPACE.size12 : 0,
    compact: true,
    hideIcon: true
  }, /*#__PURE__*/React.createElement(Flex, null, /*#__PURE__*/React.createElement(Box, {
    pr: 2
  }, "\u2605"), "Generally requires elite level status.")) : null, /*#__PURE__*/React.createElement(ContentPadding, null, hasOurSeatAmenityData ? seatAmenities === null || seatAmenities === void 0 ? void 0 : seatAmenities.map(amenity => /*#__PURE__*/React.createElement(Flex, {
    key: amenity.description,
    alignItems: "center",
    py: "4px"
  }, /*#__PURE__*/React.createElement(Flex, {
    flex: "0 0 22px"
  }, amenity.icon), /*#__PURE__*/React.createElement(Text, {
    size: 14
  }, amenity.description))) : null, /*#__PURE__*/React.createElement(SeatPerksAndDisadvantages, {
    suppressAmenities: hasOurSeatAmenityData,
    items: features
  }), /*#__PURE__*/React.createElement(SeatMeasurement, null, measurements.map(measurement => {
    var _measurement$value;
    return {
      ...measurement,
      // NaN can be returned from the API, so we need to replace it with a dash
      value: (_measurement$value = measurement.value) !== null && _measurement$value !== void 0 && _measurement$value.toLowerCase().includes('nan') ? '- -' : measurement.value
    };
  }).map(_ref4 => {
    let {
      uniqId,
      title,
      icon,
      value
    } = _ref4;
    return /*#__PURE__*/React.createElement(SeatMeasurementRegion, {
      key: uniqId
    }, /*#__PURE__*/React.createElement(SetMetricMeasurements, null, /*#__PURE__*/React.createElement(Text, {
      weight: "800"
    }, value)), /*#__PURE__*/React.createElement(Flex, {
      alignItems: "center"
    }, icon ? /*#__PURE__*/React.createElement(SeatMeasurementIcon
    // eslint-disable-next-line react/no-danger
    , {
      dangerouslySetInnerHTML: {
        __html: icon
      }
    }) : null, /*#__PURE__*/React.createElement(SeatMeasurementLabel, {
      size: 14
    }, title)));
  }))), devModeEnabled ? /*#__PURE__*/React.createElement(ContentPadding, null, /*#__PURE__*/React.createElement(OurData, null, /*#__PURE__*/React.createElement("b", null, cabinBrandName), !hasOurSeatAmenityData && !seatPitch && !seatWidth ? /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Text, {
    color: "red",
    size: 13,
    weight: "800"
  }, "We do not have any information.")) : null, seatAmenities === null || seatAmenities === void 0 ? void 0 : seatAmenities.map(amenity => /*#__PURE__*/React.createElement(Flex, {
    key: amenity.description,
    alignItems: "center",
    py: "2px"
  }, /*#__PURE__*/React.createElement(Flex, {
    flex: "0 0 22px"
  }, amenity.icon), /*#__PURE__*/React.createElement(Text, {
    size: 14
  }, amenity.description))), /*#__PURE__*/React.createElement(SeatMeasurement, null, generateAmtravSeatMeasurements(seatPitch, seatWidth).map(_ref5 => {
    let {
      uniqId,
      title,
      icon,
      value
    } = _ref5;
    return /*#__PURE__*/React.createElement(SeatMeasurementRegion, {
      key: uniqId
    }, /*#__PURE__*/React.createElement(SetMetricMeasurements, null, /*#__PURE__*/React.createElement(Text, {
      size: 14,
      weight: "800"
    }, value)), /*#__PURE__*/React.createElement(Flex, {
      alignItems: "center"
    }, icon ? /*#__PURE__*/React.createElement(SeatMeasurementIcon
    // eslint-disable-next-line react/no-danger
    , {
      dangerouslySetInnerHTML: {
        __html: icon
      }
    }) : null, /*#__PURE__*/React.createElement(SeatMeasurementLabel, {
      size: 12
    }, title)));
  }))), /*#__PURE__*/React.createElement(TheirData, null, /*#__PURE__*/React.createElement("b", null, rowName), /*#__PURE__*/React.createElement(SeatPerksAndDisadvantages, {
    items: features
  }), /*#__PURE__*/React.createElement(SeatMeasurement, null, measurements.map(_ref6 => {
    let {
      uniqId,
      title,
      icon,
      value
    } = _ref6;
    return /*#__PURE__*/React.createElement(SeatMeasurementRegion, {
      key: uniqId
    }, /*#__PURE__*/React.createElement(SetMetricMeasurements, null, /*#__PURE__*/React.createElement(Text, {
      size: 14,
      weight: "800"
    }, value)), /*#__PURE__*/React.createElement(Flex, {
      alignItems: "center"
    }, icon ? /*#__PURE__*/React.createElement(SeatMeasurementIcon
    // eslint-disable-next-line react/no-danger
    , {
      dangerouslySetInnerHTML: {
        __html: icon
      }
    }) : null, /*#__PURE__*/React.createElement(SeatMeasurementLabel, {
      size: 12
    }, title)));
  })))) : null, showSelectButton ? /*#__PURE__*/React.createElement(Box, {
    mt: 3,
    mb: 1
  }, /*#__PURE__*/React.createElement(Button, {
    fullWidth: true,
    kind: isSeatSelected ? 'danger' : 'success',
    onClick: onSelectClick,
    text: isSeatSelected ? `Clear seat ${number}` : `Select seat ${number}`
  })) : null);
};
SeatTooltipContent.propTypes = {
  customSeatData: PropTypes.shape({
    seatPrice: PropTypes.number,
    outOfPolicyReason: PropTypes.string
  }),
  onSelectClick: PropTypes.func,
  showSelectButton: PropTypes.bool,
  isSeatSelected: PropTypes.bool,
  data: PropTypes.shape({
    rowName: PropTypes.string,
    number: PropTypes.string,
    measurements: PropTypes.arrayOf(PropTypes.shape({})),
    features: PropTypes.arrayOf(PropTypes.shape({}))
  })
};

const AmtravSeatDataContext = /*#__PURE__*/createContext([]);

const DEFAULT_ARMREST_COLOR = '#bbb';
const DEFAULT_FILL_COLOR = 'white';
const SELECTED_DARK_COLOR = 'var(--base-color-success)';
const SELECTED_LIGHT_COLOR = 'var(--base-color-success-background)';
function getArmrestColor(_ref) {
  let {
    status,
    selected,
    style
  } = _ref;
  if (selected) {
    return SELECTED_DARK_COLOR;
  }
  return status === ENTITY_STATUS_MAP.available ? DEFAULT_ARMREST_COLOR : style.armrestColor;
}
function getFillColor(_ref2) {
  let {
    status,
    selected,
    style
  } = _ref2;
  if (selected) {
    return SELECTED_LIGHT_COLOR;
  }
  return status === ENTITY_STATUS_MAP.available ? DEFAULT_FILL_COLOR : style.fillColor;
}
function getStrokeColor(_ref3) {
  let {
    status,
    selected,
    style
  } = _ref3;
  if (selected) {
    return SELECTED_DARK_COLOR;
  }
  return status === ENTITY_STATUS_MAP.available ? style.fillColor : style.strokeColor;
}
const AmtravSeatIcon = _ref4 => {
  let {
    status,
    seatType,
    style,
    selected
  } = _ref4;
  const dynamicStyle = {
    armrestColor: getArmrestColor({
      status,
      selected,
      style
    }),
    fillColor: getFillColor({
      status,
      selected,
      style
    }),
    strokeColor: getStrokeColor({
      status,
      selected,
      style
    }),
    strokeWidth: style.strokeWidth
  };
  return /*#__PURE__*/React.createElement("div", {
    className: "jets-seat-svg"
    // eslint-disable-next-line react/no-danger
    ,
    dangerouslySetInnerHTML: {
      __html: seatTemplateService.getSeatIcon(seatType, dynamicStyle)
    }
  });
};
AmtravSeatIcon.propTypes = {
  status: PropTypes.string,
  seatType: PropTypes.string,
  selected: PropTypes.bool,
  style: PropTypes.shape({
    armrestColor: PropTypes.string,
    fillColor: PropTypes.string,
    strokeColor: PropTypes.string,
    strokeWidth: PropTypes.number
  })
};

const IconStyle = styled.div`
  color: #067e53;
  transform: scale(1.4) translate(0px, 2px);

  && svg {
    height: 1em;
    width: 1em;
  }
`;
const SelectedSeatIcon = () => {
  return /*#__PURE__*/React.createElement(IconStyle, null, /*#__PURE__*/React.createElement(IconCheckCircle, null));
};

const EliteStar = styled.div`
  font-size: 35px;
  color: ${getThemeProp('global.textBody--color')};
  opacity: 0.8;
`;
const EliteStatusStar = () => {
  return /*#__PURE__*/React.createElement(EliteStar, null, "\u2605");
};

const SeatNum = styled.b`
  ${_ref => {
  let {
    isPreview
  } = _ref;
  return isPreview ? css`
          opacity: 0.6;
          color: ${getThemeProp('global.textBody--color')};
        ` : css`
          color: ${getThemeProp('global.link--color')};
        `;
}};
`;
function renderSeatNumber(_ref2) {
  let {
    isEliteSeat,
    selected,
    isPreview,
    seatNumber
  } = _ref2;
  if (selected) {
    return /*#__PURE__*/React.createElement(SelectedSeatIcon, null);
  }
  if (isEliteSeat) {
    return /*#__PURE__*/React.createElement(EliteStatusStar, null);
  }
  return /*#__PURE__*/React.createElement(SeatNum, {
    isPreview: isPreview
  }, seatNumber);
}
const SeatNumber = _ref3 => {
  let {
    isEliteSeat,
    selected,
    isPreview,
    seatNumber
  } = _ref3;
  return renderSeatNumber({
    isEliteSeat,
    selected,
    isPreview,
    seatNumber
  });
};

const SeatPrice = styled.div`
  --dark-pin-background-color: #424242;

  background: var(--dark-pin-background-color);
  display: flex;
  transform: translate(0, -105px);
  color: white;
  padding: 4px 10px;
  font-family: 'Open Sans', sans-serif;
  font-size: 30px;
  border-radius: 12px;
  min-width: 70px;
  text-align: center;
  display: flex;
  justify-content: center;
  font-weight: 600;
  position: relative;
  border: 2px solid white;
  filter: drop-shadow(rgb(190, 190, 190) 0px 2px 2px);
  z-index: 1;
`;
const PriceArrow = styled.div`
  position: absolute;
  bottom: -7px;
  left: calc(50% - 8px);
  width: 0px;
  height: 0px;
  border-top: 8px solid var(--dark-pin-background-color);
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  filter: drop-shadow(rgb(255, 255, 255) 0px 2px 0px);
`;
const DollarSign = styled.span`
  font-size: 24px;
  transform: translate(0, 2px);
`;
const SeatPricePin = _ref => {
  let {
    seatPrice,
    seatNumber,
    isPreview,
    selected,
    isEliteSeat
  } = _ref;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(SeatNumber, {
    selected: selected,
    isEliteSeat: isEliteSeat,
    isPreview: isPreview,
    seatNumber: seatNumber
  }), /*#__PURE__*/React.createElement(SeatPrice, null, /*#__PURE__*/React.createElement(DollarSign, null, "$"), " ", seatPrice, /*#__PURE__*/React.createElement(PriceArrow, null)));
};
SeatPricePin.propTypes = {
  seatPrice: PropTypes.number.isRequired,
  seatNumber: PropTypes.string.isRequired,
  isPreview: PropTypes.bool,
  selected: PropTypes.bool,
  isEliteSeat: PropTypes.bool
};

/* eslint-disable react/prop-types */
const SeatWrapper$1 = styled.div`
  display: block;
  text-align: center;
`;
const BlockSeatIcon = styled(IconFlex)`
  color: black;
  padding-top: 10px;
`;
const PopOverWrapper = _ref => {
  let {
    content,
    children,
    isPopoverOpen,
    closePopover
  } = _ref;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(MatchMedia.FineInput, null, /*#__PURE__*/React.createElement(Tooltip, {
    withPadding: false,
    kind: "light",
    placement: "top",
    content: content
  }, children)), /*#__PURE__*/React.createElement(MatchMedia.CoarseInput, null, /*#__PURE__*/React.createElement(Popover, {
    open: isPopoverOpen,
    onOpenChange: open => {
      if (!open) closePopover();
    },
    withPadding: false,
    showCloseButton: true,
    kind: "light",
    placement: "top",
    content: /*#__PURE__*/React.createElement("div", {
      className: "isPopover"
    }, content)
  }, children)));
};
const AmtravJetsSeat = _ref2 => {
  var _data$features;
  let {
    data
  } = _ref2;
  const {
    params,
    colorTheme
  } = useContext(JetsContext);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const {
    type,
    status,
    size,
    color,
    rotation,
    seatType,
    seatIconType,
    topOffset,
    leftOffset,
    number
  } = data;

  // AmtravSeatDataContext returns a JS Map() object
  const {
    seatData,
    onSeatClick,
    isPreview
  } = useContext(AmtravSeatDataContext);
  const customSeatData = (seatData === null || seatData === void 0 ? void 0 : seatData.get(number)) || {};
  const {
    seatPrice,
    isEliteSeat,
    outOfPolicy = false,
    selected = false,
    seatStatus
  } = customSeatData;
  const seatIsAvailable = seatStatus === SEAT_STATUS.available;
  const seatIsBlocked = seatStatus === SEAT_STATUS.blocked || seatStatus === SEAT_STATUS.nonExistent;
  const seatIsOccupied = seatStatus === SEAT_STATUS.occupied;
  const {
    index,
    aisle
  } = ENTITY_TYPE_MAP;
  const jetsStatus = seatIsOccupied || seatIsBlocked ? ENTITY_STATUS_MAP.unavailable : status;
  const componentClassNames = `jets-seat jets-${type} jets-${jetsStatus} jets-seat-r-${rotation}`;
  const $component = useRef();
  const isFineInput = useFineInput();
  let rtlStyle = '';
  if (params !== null && params !== void 0 && params.isHorizontal && (type === aisle || type === index)) {
    rtlStyle = params.rightToLeft ? '' : 'rotate(180deg)';
  }
  const style = {
    width: size.width,
    height: size.height,
    top: topOffset,
    left: leftOffset,
    transform: rtlStyle
  };
  const svgStyle = {
    strokeColor: colorTheme.seatStrokeColor,
    armrestColor: colorTheme.seatArmrestColor,
    fillColor: seatIsOccupied || seatIsBlocked ? colorTheme.notAvailableSeatsColor : color,
    strokeWidth: colorTheme.seatStrokeWidth
  };
  const indexContentStyle = {
    transform: `${params === null || params === void 0 ? void 0 : params.antiRotation} scale(${params.antiScale})`,
    color: colorTheme.seatLabelColor,
    zIndex: 100
  };
  if (!seatType || type === index) {
    return /*#__PURE__*/React.createElement("div", {
      style: style,
      ref: $component,
      className: componentClassNames
    }, /*#__PURE__*/React.createElement("div", {
      style: indexContentStyle
    }, " "));
  }
  return /*#__PURE__*/React.createElement(SeatWrapper$1, {
    style: style,
    ref: $component,
    className: componentClassNames
  }, /*#__PURE__*/React.createElement(PopOverWrapper, {
    isPopoverOpen: popoverOpen,
    closePopover: () => {
      setPopoverOpen(false);
    },
    content: /*#__PURE__*/React.createElement(SeatTooltipContent, {
      data: data,
      isSeatSelected: selected,
      showSelectButton: seatIsAvailable && !isFineInput && !isPreview,
      onSelectClick: () => {
        setPopoverOpen(false);
        if (seatIsAvailable) {
          onSeatClick({
            seatNumber: data.number,
            ...customSeatData
          });
        }
      },
      customSeatData: customSeatData
    })
  }, seatType && type !== index ? /*#__PURE__*/React.createElement(Flex, {
    justifyContent: "center",
    flex: "1 1 auto",
    onClick: () => {
      if (seatIsAvailable && isFineInput) {
        onSeatClick({
          seatNumber: data.number,
          ...customSeatData
        });
      }
      if (!isFineInput) {
        setPopoverOpen(true);
      }
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: `jets-seat-number ST-${seatIconType} ${outOfPolicy ? 'oop' : ''}`
  }, seatIsAvailable ? /*#__PURE__*/React.createElement(React.Fragment, null, outOfPolicy ? /*#__PURE__*/React.createElement(OutOfPolicyIcon, null) : null, seatPrice && seatPrice !== 0 ? /*#__PURE__*/React.createElement(SeatPricePin, {
    isPreview: isPreview,
    isEliteSeat: isEliteSeat,
    selected: selected,
    seatPrice: Math.ceil(seatPrice),
    seatNumber: number
  }) : /*#__PURE__*/React.createElement(SeatNumber, {
    seatNumber: number,
    isEliteSeat: isEliteSeat,
    selected: selected,
    isPreview: isPreview
  })) : /*#__PURE__*/React.createElement("b", null, (_data$features = data.features) !== null && _data$features !== void 0 && _data$features.find(a => a.key === 'reservedCrewSeat') ? /*#__PURE__*/React.createElement(BlockSeatIcon, {
    size: 28
  }, /*#__PURE__*/React.createElement(IconClear, null)) : null, seatIsOccupied ? /*#__PURE__*/React.createElement(IconLine, {
    color: "black",
    strokeWidth: 4,
    size: "36px",
    icon: "User"
  }) : null)), /*#__PURE__*/React.createElement(AmtravSeatIcon, {
    status: jetsStatus,
    isSelectabe: true,
    seatType: seatType,
    style: svgStyle,
    selected: selected
  })) : /*#__PURE__*/React.createElement("div", {
    style: indexContentStyle
  }, " ")));
};

const TIMEOUT_TO_SHOW_LOADER = 1000;
const AmtravJetsSeatLoader = () => {
  const [showLoader, setShowLoader] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowLoader(true);
    }, TIMEOUT_TO_SHOW_LOADER);
    return () => {
      clearTimeout(timer);
    };
  }, []);
  return /*#__PURE__*/React.createElement("div", {
    className: "jets-not-init"
  }, showLoader && /*#__PURE__*/React.createElement(ActivityIndicator, null));
};

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
`;
const DeckToggleItem = styled(ToggleGroup.Item)`
  font-size: 16px;
  padding: 16px 24px;
`;
const DeckToggle = _ref => {
  let {
    onValueChange
  } = _ref;
  return /*#__PURE__*/React.createElement(Wrapper, null, /*#__PURE__*/React.createElement(ToggleGroup, {
    type: "single",
    defaultValue: "lower",
    onValueChange: onValueChange
  }, /*#__PURE__*/React.createElement(DeckToggleItem, {
    value: "lower"
  }, "Lower Deck"), /*#__PURE__*/React.createElement(DeckToggleItem, {
    value: "upper"
  }, "Upper Deck")));
};
DeckToggle.propTypes = {
  onValueChange: PropTypes.func
};

const LegendContainer = styled(Flex)`
  justify-content: center;
`;
const LegendFlex = styled(Flex)`
  display: inline-flex;
  width: 100%;
  gap: 12px;
  padding: 12px;

  ${media.Medium`
    width: unset;
  `};

  ul {
    padding: 16px 0 2px 8px;
    margin: 0;

    li {
      line-height: 1.4;
      list-style: square;
    }
  }
`;
const PriceRange = styled.div`
  padding-top: 4px;
  font-family: monospace;
`;
const LegendItem = styled(Box)`
  background-color: ${getTokenValue(THEME_TOKENS.card.color.backgroundSecondary)};
  color: ${getTokenValue(THEME_TOKENS.base.color.primaryText)};
  border-radius: ${getTokenValue(THEME_TOKENS.card.size.borderRadiusLarge)};

  ul {
    color: ${getTokenValue(THEME_TOKENS.base.color.secondaryText)};
  }
`;
const SeatLegend = _ref => {
  let {
    legendData
  } = _ref;
  return /*#__PURE__*/React.createElement(LegendContainer, null, /*#__PURE__*/React.createElement(LegendFlex, {
    mt: 3,
    flexDirection: ['column', 'column', 'row']
  }, legendData.map(_ref2 => {
    let {
      brandName,
      attributes,
      minPrice,
      maxPrice
    } = _ref2;
    return /*#__PURE__*/React.createElement(LegendItem, {
      key: brandName,
      p: 3
    }, /*#__PURE__*/React.createElement("b", null, brandName, " "), maxPrice > 0 && /*#__PURE__*/React.createElement(PriceRange, null, "$", Math.round(minPrice), minPrice !== maxPrice && `–$${Math.round(maxPrice)}`), /*#__PURE__*/React.createElement("ul", null, attributes.map(attribute => /*#__PURE__*/React.createElement("li", {
      key: attribute
    }, attribute))));
  })));
};
SeatLegend.propTypes = {
  legendData: PropTypes.arrayOf(PropTypes.shape({
    brandName: PropTypes.string,
    attributes: PropTypes.arrayOf(PropTypes.string),
    maxPrice: PropTypes.number,
    minPrice: PropTypes.number
  }))
};

const SeatWrapper = styled.div`
  display: ${_ref => {
  let {
    showPlane
  } = _ref;
  return showPlane ? 'block' : 'none';
}};
`;
const ErrorContainter = styled.div`
  justify-content: center;
  display: flex;
`;
const componentOverrides = {
  JetsSeat: AmtravJetsSeat,
  JetsNotInit: AmtravJetsSeatLoader
};
function getWidth(basePlaneWidth) {
  const planeBodyWidth = basePlaneWidth / 2.5; // reduce plane width

  return window.innerWidth > planeBodyWidth ? planeBodyWidth : window.innerWidth - 32;
}
const SeatMap = _ref2 => {
  let {
    flight,
    baseUrl = '',
    seatData = new Map(),
    cabinLabels = {},
    onMapInit = () => {},
    onSeatClick = () => {},
    isPreview = false,
    legendData = [],
    hideAvailabilityNotice = false,
    customNoticeMessage = null
  } = _ref2;
  const [hasTwoDecks, setHasTwoDecks] = useState(false);
  const [activeDeckIndex, setActiveDeckIndex] = useState(0);
  const [deviceWidth, setDeviceWidth] = useState(0);
  const [seatmapInitialized, setSeatmapInitialized] = useState(false);
  const [showPlane, setShowPlane] = useState(false);
  const [showNoMapAvailable, setShowNoMapAvailable] = useState(false);
  const [hasError, setHasError] = useState(false);
  const showCabinTitles = true;
  const onMapInitialization = layoutData => {
    setHasTwoDecks(layoutData.decksCount === 2);
    setDeviceWidth(getWidth(Math.floor(layoutData.widthInPx)));
    setSeatmapInitialized(true);
    setHasError(layoutData.error);
    onMapInit(layoutData);
  };
  const updateActiveDeck = deckValue => {
    setActiveDeckIndex(deckValue === 'upper' ? 1 : 0);
  };
  const amtravSeatContextData = React.useMemo(() => ({
    seatData,
    onSeatClick,
    isPreview
  }), [seatData]);
  const availability = [...seatData.keys()].map(key => ({
    label: key
  }));

  // This object is used as an dependency
  const memoedFlight = useMemo(() => {
    return {
      ...flight,
      cabinClass: 'A',
      id: '0'
    };
  }, [flight.id, deviceWidth]);
  useEffect(() => {
    // A timeout is added to prevent seeing the plane rendered twice at two different widths
    if (seatmapInitialized) {
      setTimeout(() => {
        setShowPlane(seatmapInitialized);
        setShowNoMapAvailable(!hideAvailabilityNotice && availability.length === 0);
      }, 2100);
    }
  }, [seatmapInitialized]);
  if (hasError) {
    return /*#__PURE__*/React.createElement(ErrorContainter, null, /*#__PURE__*/React.createElement(Notice, {
      mb: 3,
      kind: "warning"
    }, "Unable to retrieve the seat map."));
  }
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(SeatStyles, {
    showCabinTitles: showCabinTitles
  }), /*#__PURE__*/React.createElement(AmtravSeatDataContext.Provider, {
    value: amtravSeatContextData
  }, !showPlane && !showNoMapAvailable ? /*#__PURE__*/React.createElement(Box, {
    pt: 4
  }, /*#__PURE__*/React.createElement(ActivityIndicator, null)) : null, /*#__PURE__*/React.createElement(SeatWrapper, {
    showPlane: showPlane
  }, customNoticeMessage ? /*#__PURE__*/React.createElement(ErrorContainter, null, /*#__PURE__*/React.createElement(Notice, {
    maxWidth: "500px",
    mb: 3
  }, customNoticeMessage)) : null, showNoMapAvailable && !customNoticeMessage && /*#__PURE__*/React.createElement(Notice, {
    mb: 3,
    kind: "warning"
  }, "Unable to check seat availability."), hasTwoDecks && /*#__PURE__*/React.createElement(DeckToggle, {
    onValueChange: updateActiveDeck
  }), legendData.length ? /*#__PURE__*/React.createElement(SeatLegend, {
    legendData: legendData
  }) : null, /*#__PURE__*/React.createElement(JetsSeatMap, {
    config: {
      ...SeatConfig,
      apiUrl: `${baseUrl}${SeatConfig.apiUrl}`,
      customCabinTitles: cabinLabels,
      width: deviceWidth,
      visibleCabinTitles: showCabinTitles
    },
    currentDeckIndex: activeDeckIndex,
    flight: memoedFlight,
    availability: availability,
    componentOverrides: componentOverrides,
    onSeatMapInited: onMapInitialization,
    onSeatSelected: () => {},
    onSeatUnselected: () => {},
    onTooltipRequested: () => {},
    onLayoutUpdated: () => {},
    onSeatMouseLeave: () => {},
    onSeatMouseClick: () => {}
  }))));
};
SeatMap.propTypes = {
  flight: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  }).isRequired,
  baseUrl: PropTypes.string,
  seatData: PropTypes.instanceOf(Map),
  /**
   * fires when seatmap is initialized, includes error (message) when a failure occurs
   */
  onMapInit: PropTypes.func,
  /**
   * returns the selected seatData for that seat along with the `seatNumber`
   */
  onSeatClick: PropTypes.func,
  isPreview: PropTypes.bool,
  legendData: PropTypes.arrayOf(PropTypes.shape({})),
  hideAvailabilityNotice: PropTypes.bool,
  cabinLabels: PropTypes.shape({}),
  /**
   *  A custom message to show above the seat map.
   */
  customNoticeMessage: PropTypes.string
};

// code for modal with placeholder text
const QuestionText$1 = styled(Text)`
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 10px;
  margin-top: 10px;
`;
const AnswerText = styled(Text)`
  font-size: 16px;
  margin-bottom: 20px;
  line-height: 1.4;
`;
const ModalBodyContainer = styled.div`
  max-width: 600px;
  margin-bottom: -10px;
`;
const SelectionTipsModal = _ref => {
  let {
    onClose
  } = _ref;
  return /*#__PURE__*/React.createElement(Modal, {
    onClose: onClose
  }, /*#__PURE__*/React.createElement(Modal.Header, null, "Seat Selection Information"), /*#__PURE__*/React.createElement(ModalBodyContainer, null, /*#__PURE__*/React.createElement(Modal.Body, null, /*#__PURE__*/React.createElement(QuestionText$1, null, `Don't see any seats?`), /*#__PURE__*/React.createElement(AnswerText, null, `If no seats are available for advanced assignment, you are still guaranteed a seat on the plane, but not a
            specific one. Airlines limit pre-reserved seats and hold some for airport control. You can try to use the
            airline's online check-in within 24 hours of your flight when more seats are usually released.`), /*#__PURE__*/React.createElement(QuestionText$1, null, `Don't like the seats you see?`), /*#__PURE__*/React.createElement(AnswerText, null, `Pre-reserving the best seats is becoming harder because airlines reserve them for elite members, sell them
            for extra fees, or hold them for flight day. If you're unhappy with your seat, use online check-in within 24
            hours of your flight when more seats are often available.`))));
};
SelectionTipsModal.propTypes = {
  onClose: PropTypes.func.isRequired
};

const QuestionText = styled(Text)`
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 10px;
  margin-top: 10px;
  color: ${getThemeProp('global.dark--color')};
  text-decoration: underline;
  padding-right: 10px;
  border-radius: 50px;

  ${media.Medium`
    padding-right: 0px;
  `};
`;
const SelectionTipsTriggerText = "Can't find seats you like?";
const SelectionTipsTrigger = () => {
  const [isBottom, setIsBottom] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      if (scrollTop + windowHeight >= documentHeight - 750) {
        setIsBottom(true);
      } else {
        setIsBottom(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  return /*#__PURE__*/React.createElement(Fade, {
    show: isBottom
  }, /*#__PURE__*/React.createElement(QuestionText, {
    className: "bottom-element"
  }, SelectionTipsTriggerText));
};

const TrayContentContainer = styled.div`
  width: 100%;
  display: grid;
  grid-template-areas:
    'airports total'
    'seats  cancel'
    'seats actions'
    'instructions  actions';
  grid-template-columns: 60% 40%;
  grid-template-rows: repeat(2, minmax(25px, auto)) repeat(2, minmax(20px, auto));
  font-size: 14px;
  padding: 16px;

  ${media.Medium`
    font-size: 16px;
  `};
`;
const PrevNextButton = styled(Button)`
  width: 54px;
`;
const TextLink = styled.button`
  all: unset;
  white-space: nowrap;
  text-decoration: underline;
  cursor: pointer;
`;
const FlexArea = styled.div`
  display: flex;
`;
const LeftTop = styled(FlexArea)`
  grid-area: airports;
  align-items: flex-start;
`;
const LeftMiddle = styled(Flex)`
  grid-area: seats;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;
const LeftBottom = styled(FlexArea)`
  grid-area: instructions;
  align-items: flex-end;
`;
const RightTop = styled(FlexArea)`
  grid-area: total;
  justify-content: flex-end;
`;
const RightMiddle = styled(Flex)`
  grid-area: cancel;
  justify-content: flex-end;
  gap: 8px;
`;
const RightBottom = styled(FlexArea)`
  grid-area: actions;
  align-items: flex-end;
  justify-content: flex-end;
`;
const NavButtons = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
`;
const Total = styled.span``;
const Airport = styled.div`
  display: inline-flex;
  font-size: 20px;
  font-weight: bold;
  text-transform: uppercase;
`;
const AirlineAirports = styled.div`
  display: flex;
  align-items: center;
  white-space: nowrap;
  gap: 4px;
  transform: translateY(-4px);
`;
const SeatTile = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 24px;
  background-color: ${getTokenValue(THEME_TOKENS.base.color.success)};
  font-weight: bold;
  color: white;
  white-space: nowrap;
  padding: 0 4px;
  border-radius: 4px;
`;
const getNavButtons = (onPrevious, onNext, isFirstLeg, isLastLeg, notAllSelected, hasTotal, onSaveExit) => {
  const mobileSaveLabel = hasTotal ? 'Buy' : 'Save';
  const saveLabel = hasTotal ? 'Purchase' : 'Save Seats';
  return /*#__PURE__*/React.createElement(NavButtons, null, /*#__PURE__*/React.createElement(MatchMedia.SmallAndLess, null, !isFirstLeg && /*#__PURE__*/React.createElement(PrevNextButton, {
    onClick: onPrevious,
    bordered: true,
    iconLineName: "ChevronLeft",
    size: "inlineForm"
  }), /*#__PURE__*/React.createElement(Button, {
    onClick: isLastLeg ? onSaveExit : onNext,
    suffixIconLineName: isLastLeg ? null : 'ChevronRight',
    text: isLastLeg ? mobileSaveLabel : 'Next',
    disabled: isLastLeg && notAllSelected,
    size: "inlineForm"
  })), /*#__PURE__*/React.createElement(MatchMedia.Medium, null, !isFirstLeg && /*#__PURE__*/React.createElement(Button, {
    onClick: onPrevious,
    bordered: true,
    prefixIconLineName: "ChevronLeft",
    text: "Prev"
  }), /*#__PURE__*/React.createElement(Button, {
    onClick: isLastLeg ? onSaveExit : onNext,
    suffixIconLineName: isLastLeg ? null : 'ChevronRight',
    text: isLastLeg ? saveLabel : 'Next',
    disabled: isLastLeg && notAllSelected
  })));
};
const seatsDefault = [{
  seatNum: null,
  price: 0
}];
const SeatSelectionTray = _ref => {
  let {
    airlineCode,
    fromCode,
    toCode,
    seats = seatsDefault,
    instructions = '',
    isFirstLeg = true,
    isLastLeg = false,
    // isModified = false,
    notAllSelected = true,
    total = 0,
    headerMessage = null,
    headerKind = 'warning',
    onNext = () => {},
    onPrevious = () => {},
    onSaveExit = () => {},
    onCancel = () => {}
  } = _ref;
  const hasTotal = Boolean(total);
  const [selectionTipsModalOpen, setSelectionTipsModalOpen] = React.useState(false);
  return /*#__PURE__*/React.createElement(React.Fragment, null, selectionTipsModalOpen ? /*#__PURE__*/React.createElement(SelectionTipsModal, {
    onClose: () => setSelectionTipsModalOpen(false)
  }) : null, /*#__PURE__*/React.createElement(Tray, {
    minHeight: "125px",
    maxWidth: "1260px",
    mx: [0, 0, '12px', 4],
    headerMessage: headerMessage,
    headerKind: headerKind,
    contentAboveTray: /*#__PURE__*/React.createElement(Flex, {
      justifyContent: "flex-end"
    }, /*#__PURE__*/React.createElement(TextLink, {
      onClick: () => setSelectionTipsModalOpen(true)
    }, /*#__PURE__*/React.createElement(SelectionTipsTrigger, null))),
    content: /*#__PURE__*/React.createElement(TrayContentContainer, null, /*#__PURE__*/React.createElement(LeftTop, null, /*#__PURE__*/React.createElement(AirlineAirports, null, /*#__PURE__*/React.createElement(AirlineLogo, {
      alCode: airlineCode,
      size: "21px"
    }), "\xA0", /*#__PURE__*/React.createElement(Airport, null, fromCode), /*#__PURE__*/React.createElement(IconLine, {
      icon: "ArrowRight"
    }), /*#__PURE__*/React.createElement(Airport, null, toCode))), /*#__PURE__*/React.createElement(LeftMiddle, {
      pt: [1, 1, 0]
    }, seats[0].seatNum ? seats.map(s => {
      return /*#__PURE__*/React.createElement(SeatTile, {
        key: s.seatNum
      }, s.seatNum, s.price && s.price !== 0 ? /*#__PURE__*/React.createElement("span", null, "\xA0\xB7", ` $${s.price}`) : null);
    }) : null), /*#__PURE__*/React.createElement(LeftBottom, null, /*#__PURE__*/React.createElement(Text, {
      mt: "8px",
      mb: "-4px",
      lineHeight: 1.3
    }, instructions)), /*#__PURE__*/React.createElement(RightTop, null, hasTotal && total !== 0 ? /*#__PURE__*/React.createElement(Total, null, "Total: ", /*#__PURE__*/React.createElement("strong", null, "$", total)) : ''), /*#__PURE__*/React.createElement(RightMiddle, {
      pb: [2, 0],
      flexDirection: ['column', 'row'],
      alignItems: ['flex-end', 'flex-start']
    }, /*#__PURE__*/React.createElement(TextLink, {
      onClick: onCancel
    }, "Cancel"), isLastLeg || notAllSelected ? null : /*#__PURE__*/React.createElement(TextLink, {
      onClick: onSaveExit
    }, hasTotal ? /*#__PURE__*/React.createElement("strong", null, "Purchase & Exit") : /*#__PURE__*/React.createElement("strong", null, "Save & Exit"))), /*#__PURE__*/React.createElement(RightBottom, null, getNavButtons(onPrevious, onNext, isFirstLeg, isLastLeg, notAllSelected, hasTotal, onSaveExit)))
  }));
};
SeatSelectionTray.propTypes = {
  airlineCode: PropTypes.string.isRequired,
  fromCode: PropTypes.string.isRequired,
  toCode: PropTypes.string.isRequired,
  seats: PropTypes.arrayOf(PropTypes.shape({})),
  /** This will typically show the user how many seats are left for them to pick */
  instructions: PropTypes.string,
  /** Disables the "Prev" nav button */
  isFirstLeg: PropTypes.bool,
  isLastLeg: PropTypes.bool,
  /** This will show "Selected Seats" if true so the user knows they have just picked new seats for that segment */
  // isModified: PropTypes.bool,
  /** Disables the final action for saving or purchasing seats */
  notAllSelected: PropTypes.bool,
  /** If the seats cost money to change or upgrade, then we will display the total for purchasing */
  total: PropTypes.number,
  /**
   * Display a message using the Notice component.
   */
  headerMessage: PropTypes.string,
  headerKind: PropTypes.string,
  onNext: PropTypes.func.isRequired,
  onPrevious: PropTypes.func,
  onSaveExit: PropTypes.func,
  /** Cancels the seat selection process without saving */
  onCancel: PropTypes.func.isRequired
};

const CitiesContainer = styled.div`
  gap: 4px;
  display: grid;
  max-width: 340px;
`;
const Cities = styled.div`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: bold;
  line-height: 1.2;
`;
const AircraftType = styled.div`
  color: ${getThemeProp('global.greyPalette.700')};
`;
const DateTimeContainer = styled.div`
  gap: 4px;
  display: flex;
  color: ${getThemeProp('global.greyPalette.700')};
`;
const SelectedFlightInfoContainer = styled(Box)`
  padding: 0 4px 24px;
  font-size: 12px;
  width: 100%;
  color: ${getTokenValue(THEME_TOKENS.base.color.primaryText)};

  ${media.Medium`
    font-size: 16px;
  `};
`;
const CityDateTimeContainer = styled(Flex)`
  gap: 4px;
`;
const FlightNumberAircraftContainer = styled(Flex)`
  gap: 4px;
`;
const NoticeOperatedBy = styled(Notice)`
  display: inline-flex;
`;
const SelectedLegInfo = _ref => {
  let {
    departureCity,
    arrivalCity,
    departureDateDisplay,
    departureTimeDisplay,
    duration,
    flightNumber,
    aircraftType,
    airlineCode,
    opAirlineName,
    opAirlineCode
  } = _ref;
  return /*#__PURE__*/React.createElement(SelectedFlightInfoContainer, null, /*#__PURE__*/React.createElement(Flex, {
    flex: "1 0 auto",
    justifyContent: "space-between",
    flexDirection: ['column', 'column', 'row']
  }, /*#__PURE__*/React.createElement(CityDateTimeContainer, {
    flexDirection: "column"
  }, /*#__PURE__*/React.createElement(CitiesContainer, null, /*#__PURE__*/React.createElement(Cities, null, departureCity, " to ", arrivalCity)), /*#__PURE__*/React.createElement(DateTimeContainer, null, /*#__PURE__*/React.createElement(Text, null, departureDateDisplay, " at ", departureTimeDisplay, " (", duration, ")"))), /*#__PURE__*/React.createElement(FlightNumberAircraftContainer, {
    flexDirection: ['row', 'row', 'column'],
    mt: [2, 2, 0],
    alignItems: ['center', 'center', 'end']
  }, /*#__PURE__*/React.createElement(Text, {
    weight: "bold"
  }, flightNumber), /*#__PURE__*/React.createElement(AircraftType, null, aircraftType))), opAirlineCode !== airlineCode && /*#__PURE__*/React.createElement(Flex, {
    justifyContent: ['start', 'start', 'end']
  }, /*#__PURE__*/React.createElement(NoticeOperatedBy, {
    hideIcon: true,
    compact: true,
    mt: 2,
    w: "auto",
    light: true
  }, /*#__PURE__*/React.createElement(Flex, null, /*#__PURE__*/React.createElement(AirlineLogo, {
    alCode: opAirlineCode,
    size: "16px"
  }), /*#__PURE__*/React.createElement(Box, {
    pl: 2
  }, "Operated by ", opAirlineName)))));
};
SelectedLegInfo.propTypes = {
  departureCity: PropTypes.string,
  arrivalCity: PropTypes.string,
  departureDateDisplay: PropTypes.string,
  departureTimeDisplay: PropTypes.string,
  duration: PropTypes.string,
  flightNumber: PropTypes.string,
  aircraftType: PropTypes.string,
  airlineCode: PropTypes.string,
  opAirlineCode: PropTypes.string,
  opAirlineName: PropTypes.string
};

const GrayscaleContainer = styled(Flex)`
  filter: ${props => !props.isSelected ? 'grayscale(1)' : 'none'};
  opacity: ${props => !props.isSelected ? '.6' : '1'};
  justify-content: center;
`;
const FlightLegDisplayContainer = styled.div`
  display: flex;
  padding: 0px 12px 0px 12px;
  gap: 2px;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  color: ${getThemeProp('global.greyPalette.700')};
  &:hover ${GrayscaleContainer} {
    filter: grayscale(0);
    opacity: 1;
  }
  ${media.Small`
    gap: 8px;
    padding-bottom: 10px;
    width: 100%;
    color: unset;
  `};
`;
const FlightLegDisplay = _ref => {
  let {
    departureAirport,
    arrivalAirport,
    airlineCode,
    isSelected
  } = _ref;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(MatchMedia.Medium, null, /*#__PURE__*/React.createElement(FlightLegDisplayContainer, null, /*#__PURE__*/React.createElement(GrayscaleContainer, {
    isSelected: isSelected
  }, /*#__PURE__*/React.createElement(AirlineLogo, {
    alCode: airlineCode,
    size: "20px"
  })), /*#__PURE__*/React.createElement(AirportCode, {
    code: departureAirport
  }), /*#__PURE__*/React.createElement(IconLine, {
    icon: "ArrowRight"
  }), /*#__PURE__*/React.createElement(AirportCode, {
    code: arrivalAirport
  }))), /*#__PURE__*/React.createElement(MatchMedia.SmallAndLess, null, /*#__PURE__*/React.createElement(FlightLegDisplayContainer, null, /*#__PURE__*/React.createElement(Flex, {
    pr: 2
  }, /*#__PURE__*/React.createElement(AirlineLogo, {
    alCode: airlineCode,
    size: "18px"
  })), /*#__PURE__*/React.createElement(AirportCode, {
    code: departureAirport
  }), /*#__PURE__*/React.createElement(IconLine, {
    icon: "ArrowRight"
  }), /*#__PURE__*/React.createElement(AirportCode, {
    code: arrivalAirport
  }))));
};
FlightLegDisplay.propTypes = {
  departureAirport: PropTypes.string.isRequired,
  arrivalAirport: PropTypes.string.isRequired,
  airlineCode: PropTypes.string.isRequired,
  isSelected: PropTypes.bool.isRequired
};

// import { SeatMapDataPropTypes, LegDataPropTypes } from '../PropTypesConst';

const FlightLegSelectorTabTrigger = _ref => {
  let {
    legList,
    selectedLeg,
    setSelectedLeg
  } = _ref;
  const [disableBackButton, setDisableBackButton] = useState(false);
  const [disableNextButton, setDisableNextButton] = useState(false);
  const {
    flight: selectedFlight
  } = selectedLeg;
  const handleTabClicked = leg => {
    setSelectedLeg(leg);
  };
  useEffect(() => {
    const currentLegId = selectedFlight.id;
    const currentIndex = legList.findIndex(leg => leg.id === currentLegId);
    if (currentIndex === -1) {
      return;
    }
    if (currentIndex === 0) {
      setDisableBackButton(true);
      setDisableNextButton(false);
      return;
    }
    if (currentIndex === legList.length - 1) {
      setDisableBackButton(false);
      setDisableNextButton(true);
      return;
    }
    setDisableBackButton(false);
    setDisableNextButton(false);
  }, [selectedLeg]);
  const setLegToNext = () => {
    const flightLegs = legList;
    const currentLegId = selectedFlight.id;
    const currentIndex = flightLegs.findIndex(leg => leg.id === currentLegId);
    if (currentIndex === -1) {
      return;
    }
    const nextIndex = (currentIndex + 1) % flightLegs.length;
    const nextLeg = flightLegs[nextIndex];
    setSelectedLeg(nextLeg);
  };
  const setLegToPrevious = () => {
    const flightLegs = legList;
    const currentLegId = selectedFlight.id;
    const currentIndex = flightLegs.findIndex(leg => leg.id === currentLegId);
    if (currentIndex === -1) {
      return;
    }
    const previousIndex = (currentIndex - 1) % flightLegs.length;
    const previousLeg = flightLegs[previousIndex];
    setSelectedLeg(previousLeg);
  };
  const themeContext = useContext(ThemeContext);
  const isMobileOrSmaller = isMatchedMaxWidth(`${themeContext.global['breakpointSM--size']}px`);
  return /*#__PURE__*/React.createElement(Box, {
    pt: isMobileOrSmaller ? 0 : 3
  }, /*#__PURE__*/React.createElement(Tabs.List, {
    customTabSwitchingControls: isMobileOrSmaller ? /*#__PURE__*/React.createElement(Flex, {
      justifyContent: "space-around",
      backgroundColor: "white",
      py: 3
    }, /*#__PURE__*/React.createElement(Flex, {
      justifyContent: "center",
      alignItems: "center"
    }, /*#__PURE__*/React.createElement(Button, {
      onClick: setLegToPrevious,
      icon: /*#__PURE__*/React.createElement(IconPrevious, null),
      disabled: disableBackButton,
      round: true
    })), /*#__PURE__*/React.createElement(Box, {
      pt: "6px"
    }, /*#__PURE__*/React.createElement(FlightLegDisplay, {
      departureAirport: selectedLeg.departureAirport,
      arrivalAirport: selectedLeg.arrivalAirport,
      airlineCode: selectedLeg.airlineCode,
      isSelected: true
    })), /*#__PURE__*/React.createElement(Flex, {
      justifyContent: "center",
      alignItems: "center"
    }, /*#__PURE__*/React.createElement(Button, {
      onClick: setLegToNext,
      icon: /*#__PURE__*/React.createElement(IconNext, null),
      disabled: disableNextButton,
      round: true
    }))) : null
  }, legList.map(leg => {
    return /*#__PURE__*/React.createElement(Tabs.Trigger, {
      grow: true,
      value: leg.id,
      kind: "primary",
      onClick: () => handleTabClicked(leg),
      key: leg.id
    }, /*#__PURE__*/React.createElement(FlightLegDisplay, {
      departureAirport: leg.departureAirport,
      arrivalAirport: leg.arrivalAirport,
      airlineCode: leg.airlineCode,
      isSelected: selectedLeg.id === leg.id
    }));
  })));
};

export { FlightLegDisplay, FlightLegSelectorTabTrigger, SeatLegend, SeatMap, SeatSelectionTray, SelectedLegInfo };
//# sourceMappingURL=index.esm.js.map
